/**
 * @file src/components/ImageGrid.tsx
 * Grid display of images with selection and drag & drop functionality
 */

import { useState, useEffect } from 'react'
import { useStore, File } from '../store/useStore'
import { Card, CardMedia, Typography, Box, IconButton, Dialog } from '@mui/material'
import { Fullscreen, Close, DragIndicator } from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useParams } from 'react-router-dom'

const STORAGE_KEY_PREFIX = 'fowix_pdf_order_'

interface SortableItemProps {
  file: File
  onFullscreen: (file: File) => void
  onToggleSelection: (fileId: string) => void
}

const SortableItem = ({ file, onFullscreen, onToggleSelection }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          '& .drag-handle': {
            opacity: 1,
          },
        },
      }}
    >
      <Box
        className="drag-handle"
        {...attributes}
        {...listeners}
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          color: 'white',
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: '50%',
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          opacity: 0,
          transition: 'opacity 0.2s',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.7)',
          },
          zIndex: 1,
        }}
      >
        <DragIndicator />
      </Box>

      <Box onClick={() => onToggleSelection(file.id)}>
        <CardMedia
          component="img"
          image={file.url}
          alt={file.name}
          sx={{
            aspectRatio: '1',
            objectFit: 'cover',
          }}
        />
      </Box>

      {file.selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: '#40C057',
            color: 'white',
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '0.875rem',
            zIndex: 1,
          }}
        >
          {file.order}
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          p: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {file.name}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onFullscreen(file)
          }}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Fullscreen fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  )
}

export const ImageGrid = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { files, setFiles } = useStore()
  const [fullscreenImage, setFullscreenImage] = useState<File | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Load order from localStorage on mount and when files change
  useEffect(() => {
    if (sessionId && files.length > 0) {
      const savedOrder = localStorage.getItem(`${STORAGE_KEY_PREFIX}${sessionId}`)
      if (savedOrder) {
        try {
          const orderMap = JSON.parse(savedOrder)
          const orderedFiles = [...files].sort((a, b) => {
            const orderA = orderMap[a.id] ?? Number.MAX_SAFE_INTEGER
            const orderB = orderMap[b.id] ?? Number.MAX_SAFE_INTEGER
            return orderA - orderB
          })
          
          // Only update if the order is different
          const hasOrderChanged = orderedFiles.some((file, index) => file.id !== files[index].id)
          if (hasOrderChanged) {
            setFiles(orderedFiles)
          }
        } catch (error) {
          console.error('Error loading saved order:', error)
          // Clear invalid saved order
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${sessionId}`)
        }
      }
    }
  }, [sessionId, files.length])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    
    if (over && active.id !== over.id) {
      setFiles((prevFiles) => {
        const oldIndex = prevFiles.findIndex((f) => f.id === active.id)
        const newIndex = prevFiles.findIndex((f) => f.id === over.id)
        const newFiles = arrayMove(prevFiles, oldIndex, newIndex)

        // Save new order to localStorage
        if (sessionId) {
          const orderMap = newFiles.reduce((acc, file, index) => {
            acc[file.id] = index
            return acc
          }, {} as Record<string, number>)
          try {
            localStorage.setItem(`${STORAGE_KEY_PREFIX}${sessionId}`, JSON.stringify(orderMap))
          } catch (error) {
            console.error('Error saving order:', error)
          }
        }

        return newFiles
      })
    }
  }

  const toggleSelection = (fileId: string) => {
    const selectedCount = files.filter(f => f.selected).length
    
    setFiles(files.map(file => {
      if (file.id === fileId) {
        if (file.selected) {
          return { ...file, selected: false, order: null }
        }
        return { ...file, selected: true, order: selectedCount + 1 }
      }
      return file
    }))

    if (files.find(f => f.id === fileId)?.selected) {
      const removedOrder = files.find(f => f.id === fileId)?.order
      setFiles((prev: File[]) => 
        prev.map((file: File) => {
          if (file.selected && file.order && file.order > removedOrder!) {
            return { ...file, order: file.order - 1 }
          }
          return file
        })
      )
    }
  }

  if (files.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 4,
          border: '1px dashed',
          borderColor: 'grey.200',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body1" sx={{ color: '#1E2A3B', fontWeight: 600, mb: 0.5 }}>
          No images yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
          Forward your photos to Fowix on WhatsApp, then refresh this page.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={files.map(f => f.id)} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: 2,
              mb: 4,
            }}
          >
            {files.map((file) => (
              <SortableItem
                key={file.id}
                file={file}
                onFullscreen={setFullscreenImage}
                onToggleSelection={toggleSelection}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      <Dialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'black',
            position: 'relative',
          },
        }}
      >
        {fullscreenImage && (
          <>
            <IconButton
              onClick={() => setFullscreenImage(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 1,
              }}
            >
              <Close />
            </IconButton>
            <Box
              sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <img
                src={fullscreenImage.url}
                alt={fullscreenImage.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </>
        )}
      </Dialog>
    </>
  )
} 